import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  QUICK_MENU_SETTINGS_HINT,
  QUICK_MENU_SETTINGS_TITLE,
} from "@/constants/quickMenu";
import type { QuickMenuChoiceGroup } from "@/types/quickMenu";

interface QuickMenuSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 메뉴 묶음별 체크박스 줄. `selected` 가 체크 상태다. */
  groups: QuickMenuChoiceGroup[];
  /** 지금 체크된 줄 수(첫 칸 홈은 세지 않는다). */
  selectedCount: number;
  onToggle: (url: string) => void;
  /** 초안을 저장하고 닫는다. */
  onSave: () => void;
  /** 초안을 기본 목록으로 되돌린다. 저장은 따로 눌러야 한다. */
  onReset: () => void;
}

/**
 * 자주찾는 메뉴 설정 다이얼로그 — 메뉴 묶음별로 어떤 페이지를 띠에 올릴지 체크한다.
 *
 * 여기서 하는 체크는 초안일 뿐이라 `저장` 전에 닫으면 버려진다. 열림 상태와 초안은
 * 훅(`useQuickMenuSettings`)이 갖고 이 컴포넌트는 그리기만 한다.
 */
const QuickMenuSettings = ({
  open,
  onOpenChange,
  groups,
  selectedCount,
  onToggle,
  onSave,
  onReset,
}: QuickMenuSettingsProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{QUICK_MENU_SETTINGS_TITLE}</DialogTitle>
        <DialogDescription>{QUICK_MENU_SETTINGS_HINT}</DialogDescription>
      </DialogHeader>

      <div className="flex max-h-[55vh] flex-col gap-5 overflow-y-auto pr-1">
        {groups.map((group) => (
          <fieldset key={group.id} className="flex flex-col gap-1">
            <legend className="mb-1 text-xs font-medium text-muted-foreground">
              {group.label}
            </legend>
            {group.choices.map((choice) => (
              <label
                key={choice.url}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-accent"
              >
                <Checkbox
                  checked={choice.selected}
                  onCheckedChange={() => onToggle(choice.url)}
                />
                <choice.icon
                  className="size-4 text-muted-foreground"
                  aria-hidden
                />
                <span>{choice.title}</span>
              </label>
            ))}
          </fieldset>
        ))}
      </div>

      <DialogFooter className="sm:items-center">
        <span
          aria-live="polite"
          className="text-xs text-muted-foreground sm:mr-auto"
        >
          {selectedCount}개 선택
        </span>
        <Button type="button" variant="ghost" onClick={onReset}>
          기본값으로
        </Button>
        <DialogClose render={<Button type="button" variant="outline" />}>
          취소
        </DialogClose>
        <Button type="button" onClick={onSave}>
          저장
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export { QuickMenuSettings };
